import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, Image, TouchableOpacity, StyleSheet } from "react-native";
import ChevronRight from "@assets/svg/ChevronRight";
import Text from "@components/CustomText";
import color from "@constants/color";
import CpDbHOC from "@components/HOC/CpDbHOC";
import SingleIssueTooltip from "../../SingleIssueTooltip/SingleIssueTooltip";
import { useNavigation } from "@react-navigation/native";
import {
  ImagesMandatoryForSamasya,
  OrderItem,
} from "@screens/CustomerOrderItems/interface";
import { getStateDiff } from "@utils/itemMarking";
import toasts from "@utils/toast";
import { itemsQuantityStatusV3 } from "@services/api";
import IssuesImagesUpload from "@screens/MyDeliveries/components/IssuesImagesUpload";
import IssuesList from "../IssueList";

interface OrderItemProps {
  info: OrderItem;
  markingConfig: any;
  fetchData?: () => void;
  showIssueBtn?: boolean;
  imagesMandatoryForSamasya: ImagesMandatoryForSamasya;
}

const OrderItemCard: React.FC<OrderItemProps> = ({
  info,
  markingConfig,
  fetchData,
  showIssueBtn = true,
  imagesMandatoryForSamasya,
}) => {
  const navigation = useNavigation();
  const [currentStateForSingleQty, setCurrentStateForSingleQty] = useState(
    info?.current_state || {
      wrong_item_received: 0,
      item_damaged: 0,
      not_found: 0,
      cx_rto_predelivery: 0,
      ofd_db_lost_damage: 0,
    },
  );
  const [images, setImages] = useState({
    wrong_item_received:
      info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
    item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
  });
  const [reason, setReason] = useState(
    info?.current_state?.item_damaged
      ? "item_damaged"
      : info?.current_state?.wrong_item_received
        ? "wrong_item_received"
        : "",
  );
  const [visible, setVisible] = useState(false);
  const firstUpdate = useRef(true);
  const [isReverted, setIsReverted] = useState(false);

  const totalDefectiveItem =
    (info?.current_state?.item_damaged || 0) +
    (info?.current_state?.not_found || 0) +
    (info?.current_state?.cx_rto_predelivery || 0) +
    (info?.current_state?.wrong_item_received || 0) +
    (info?.current_state?.ofd_db_lost_damage || 0);
  const showImageCon = useMemo(() => {
    // if for 1 qty -> issue is marked & images are mandatory
    return (
      (imagesMandatoryForSamasya?.item_damaged
        ? currentStateForSingleQty?.item_damaged || 0
        : 0) +
        (imagesMandatoryForSamasya?.wrong_item_received
          ? currentStateForSingleQty?.wrong_item_received || 0
          : 0) >
        0 &&
      reason &&
      info?.total_quantity === 1
    );
  }, [
    currentStateForSingleQty,
    info?.total_quantity,
    reason,
    imagesMandatoryForSamasya,
  ]);
  useEffect(() => {
    firstUpdate.current = true;
    setCurrentStateForSingleQty(info?.current_state);
  }, [info?.current_state]);

  const saveMarkingData = async function () {
    try {
      if (
        showImageCon &&
        Object.values(images).reduce((acc, each) => acc + each.length, 0) === 0
      ) {
        toasts.showShortToast("कृप्या फोटो डालें!");
        setVisible(false);
        return;
      }
      const new_state = {
        item_damaged: currentStateForSingleQty.item_damaged,
        wrong_item_received: currentStateForSingleQty?.wrong_item_received,
        not_found: currentStateForSingleQty?.not_found,
        cx_rto_predelivery: currentStateForSingleQty?.cx_rto_predelivery,
        ofd_db_lost_damage: currentStateForSingleQty?.ofd_db_lost_damage,
      };

      const payload = {
        order_item_id: info?.order_item_id,
        hash: info?.hash,
        ofd_type: info?.ofd_type,
        type: info?.type,
        diff: getStateDiff(new_state, info?.current_state),
        found_marked: true,
        issue_images: {
          wrong_item_received: images?.wrong_item_received?.map(
            (i) => i?.url && i.url,
          ),
          item_damaged: images?.item_damaged?.map((i) => i?.url && i.url),
        },
      };
      await itemsQuantityStatusV3(payload);
      fetchData();
      setVisible(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (isReverted) setIsReverted(false);
    else saveMarkingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStateForSingleQty]);

  const onImageChange = (issue, issueImages) => {
    if (images[issue]) {
      setImages((p) => ({
        ...p,
        [issue]: issueImages,
      }));
    }
    // Object.values(images).reduce((acc, each) => acc + each.length, 0) > 0 &&
    //   saveMarkingData();
    setCurrentStateForSingleQty((p) => ({ ...p }));
  };

  const revertStateForSingleQty = () => {
    setImages({
      wrong_item_received:
        info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
      item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
    });
    setIsReverted(true);
    setReason(
      info?.current_state?.item_damaged
        ? "item_damaged"
        : info?.current_state?.wrong_item_received
          ? "wrong_item_received"
          : "",
    );
    setCurrentStateForSingleQty(
      info?.current_state || {
        wrong_item_received: 0,
        item_damaged: 0,
        not_found: 0,
        cx_rto_predelivery: 0,
        ofd_db_lost_damage: 0,
      },
    );
  };
  // for single quantity items
  const onCurrentStateChange = ({
    wrong_item_received = 0,
    item_damaged = 0,
    not_found = 0,
    cx_rto_predelivery = 0,
    ofd_db_lost_damage = 0,
  }) => {
    if (wrong_item_received) {
      setReason("wrong_item_received");
    }
    if (item_damaged) {
      setReason("item_damaged");
    }

    setImages({
      wrong_item_received: [],
      item_damaged: [],
    });
    setVisible(false);

    setCurrentStateForSingleQty((p) => ({
      ...p,
      wrong_item_received,
      item_damaged,
      not_found,
      cx_rto_predelivery,
      ofd_db_lost_damage,
    }));
  };
  return (
    <View style={styles.card}>
      <View style={styles.cardContent}>
        <View style={styles.imageContainer}>
          <Image source={{ uri: info?.product_image }} style={styles.image} />
          <View style={styles.quantityCirle}>
            <Text variant="caption_2" style={styles.quantityText}>
              {info?.total_quantity}
            </Text>
          </View>
        </View>
        <View style={styles.infoContainer}>
          <Text variant="body_text_4">{info?.name}</Text>
          <Text variant="body_text_4" style={styles.quantity}>
            क्वांटिटी:
            <Text variant="body_text_4" style={{ color: color.mediumBlack }}>
              {` ${info?.total_quantity}`}
            </Text>
          </Text>
        </View>
      </View>

      {showIssueBtn && (
        <View style={styles.cardFooter}>
          {info?.can_mark_v2 === "VIEW_ONLY" ? null : info?.total_quantity ===
            1 ? (
            <SingleIssueTooltip
              markingConfig={markingConfig}
              currentState={currentStateForSingleQty}
              onSubmit={onCurrentStateChange}
              visible={visible}
              setVisible={setVisible}
            />
          ) : (
            <TouchableOpacity
              style={styles.issuebtn}
              onPress={() => {
                navigation.navigate("MultiQuantityIssues", {
                  info,
                  markingConfig,
                  imagesMandatoryForSamasya,
                });
              }}
              activeOpacity={0.9}
            >
              {totalDefectiveItem > 0 ? (
                <Text variant="body_text_3" style={styles.issueText}>
                  समस्या बदलें
                </Text>
              ) : (
                <Text variant="body_text_3" style={styles.issueText}>
                  समस्या बताये
                </Text>
              )}

              <ChevronRight height={14} width={14} pathFill={color.black} />
            </TouchableOpacity>
          )}
          {totalDefectiveItem > 0 && (
            <>
              {info?.can_mark_v2 !== "VIEW_ONLY" && (
                <View style={styles.line} />
              )}
              <View style={styles.circle}>
                <Text
                  variant="caption_1"
                  style={{ color: color.white, lineHeight: 18 }}
                >
                  {totalDefectiveItem}
                </Text>
              </View>
              <ChevronRight height={14} width={14} pathFill={color.plain_red} />
              <IssuesList issuesData={info?.current_state} />
            </>
          )}
        </View>
      )}
      <View style={{ paddingLeft: 6, paddingRight: 6 }}>
        {showImageCon ? (
          <IssuesImagesUpload
            key={`${reason}_ImagesUpload`}
            reason={reason}
            imageRequired={showImageCon}
            currentImgs={images[reason] || []}
            setUploadedImage={onImageChange}
            isSingleQty={true}
            info={info}
            revertStateForSingleQty={revertStateForSingleQty}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 16,
    backgroundColor: color.white,
    borderRadius: 24,
    marginTop: 12,
    paddingVertical: 12,
    marginHorizontal: 12,
    paddingTop: 16,
  },
  cardContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  cardFooter: {
    flexDirection: "row",
    marginTop: 8,
    alignItems: "center",
  },
  image: {
    width: 50,
    height: 50,
  },
  imageContainer: {
    backgroundColor: color.primaryBg,
    borderRadius: 12,
    padding: 12,
    justifyContent: "center",
    marginRight: 12,
  },
  infoContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  quantity: {
    marginTop: 6,
    color: color.mediumBlacklightBlack,
  },
  issueText: {
    color: color.black,
    marginRight: 4,
  },
  issuebtn: {
    padding: 5,
    paddingHorizontal: 12,
    borderRadius: 18,
    flexDirection: "row",
    elevation: 2,
    backgroundColor: color.white,
    alignItems: "center",
  },
  statusContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
  },
  quantityCirle: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    backgroundColor: color.mediumBlack,
    top: -10,
    right: -6,
    padding: 2,
    paddingHorizontal: 6,
  },
  quantityText: {
    color: "white",
  },
  line: {
    width: 1,
    backgroundColor: color.lighterBlack,
    marginHorizontal: 10,
    height: "80%",
  },
  circle: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    backgroundColor: color.plain_red,
    alignItems: "center",
    justifyContent: "center",
    margin: 2,
  },
});

export default CpDbHOC(OrderItemCard);
